import React from "react";
import { Control, FieldValues, Path } from "react-hook-form";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Check, X, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { ReferralState } from "@/hooks/useReferralCode";

interface ReferralCodeFieldProps<T extends FieldValues> {
  control: Control<T>;
  name: Path<T>;
  referralState: ReferralState;
  onReferralChange: (value: string) => void;
}

export function ReferralCodeField<T extends FieldValues>({
  control,
  name,
  referralState,
  onReferralChange,
}: ReferralCodeFieldProps<T>) {
  const getInputIcon = () => {
    if (referralState.isValidating) {
      return (
        <Loader2 className="absolute right-3 top-2.5 h-4 w-4 animate-spin text-muted-foreground" />
      );
    }

    if (referralState.formCode.trim() === "") {
      return null;
    }

    if (referralState.isValid === true) {
      return (
        <Check className="absolute right-3 top-2.5 h-4 w-4 text-success" />
      );
    }

    if (referralState.isValid === false) {
      return (
        <X className="absolute right-3 top-2.5 h-4 w-4 text-destructive" />
      );
    }

    return null;
  };

  const getInputClassName = () => {
    if (referralState.formCode.trim() === "") {
      return "";
    }

    return cn(
      referralState.isValid === false &&
        "border-destructive focus-visible:ring-destructive",
      referralState.isValid === true &&
        "border-success focus-visible:ring-success",
    );
  };

  const getDescription = () => {
    if (referralState.partnerInfo && referralState.isValid) {
      return (
        <FormDescription className="text-success flex items-center gap-2">
          <Check className="h-4 w-4" />
          <span>
            Referred by:{" "}
            {referralState.partnerInfo.companyName ||
              referralState.partnerInfo.contactName}
          </span>
        </FormDescription>
      );
    }

    if (referralState.isValid === false && referralState.message) {
      return (
        <FormDescription className="text-destructive flex items-center gap-2">
          <X className="h-4 w-4" />
          {referralState.message}
        </FormDescription>
      );
    }

    if (!referralState.isLocked) {
      return (
        <FormDescription>
          Optional: Enter a referral code if you were referred by a partner
        </FormDescription>
      );
    }

    return null;
  };

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel className="flex items-center gap-2">
            Referral Code (Optional)
            {referralState.isLocked && (
              <Badge variant="secondary" className="text-xs">
                Auto-applied from link
              </Badge>
            )}
          </FormLabel>
          <FormControl>
            <div className="relative">
              <Input
                {...field}
                value={referralState.formCode}
                onChange={(e) => {
                  field.onChange(e.target.value);
                  onReferralChange(e.target.value);
                }}
                disabled={referralState.isLocked}
                placeholder={
                  referralState.isLocked
                    ? "Applied from referral link"
                    : "Enter referral code"
                }
                className={getInputClassName()}
              />
              {getInputIcon()}
            </div>
          </FormControl>
          {getDescription()}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
